import React, { useEffect, useRef, useState } from 'react';
import { Avatar } from '@leagueplatform/web-common-components';
import { Box } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

const AVATAR_SIZE = 160;
const EDIT_BADGE_SIZE = 34;

interface AvatarPickerProps {
  // Per-tenant art — analogous to the Android app's per-tenant
  // `avatar_ptth_placeholder` drawable; each app supplies its own.
  placeholderSrc: string;
  editBadgeSrc: string;
}

// No avatar-upload backend exists yet (UserProfile.avatar only accepts an
// already-resolved image_id) — this preview is local-only and never persisted.
export const AvatarPicker = ({
  placeholderSrc,
  editBadgeSrc,
}: AvatarPickerProps) => {
  const { formatMessage } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const openPicker = () => fileInputRef.current?.click();

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // The `useEffect` above already revokes the previous URL when this one
    // replaces it — no need to revoke it here too.
    setPreviewUrl(URL.createObjectURL(file));
  };

  const changePhotoLabel = formatMessage({
    id: 'STR_ACCOUNT_SETUP_CHANGE_PHOTO',
  });

  return (
    <Box
      css={{ position: 'relative', width: AVATAR_SIZE, height: AVATAR_SIZE }}
    >
      <Avatar
        imageSrc={previewUrl ?? placeholderSrc}
        altText=""
        css={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
      />
      <Box
        as="button"
        type="button"
        onClick={openPicker}
        aria-label={changePhotoLabel}
        css={{
          position: 'absolute',
          bottom: '0px',
          right: '14px',
          width: EDIT_BADGE_SIZE,
          height: EDIT_BADGE_SIZE,
          padding: 0,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          display: 'block',
        }}
      >
        <Box
          as="img"
          src={editBadgeSrc}
          alt=""
          width={EDIT_BADGE_SIZE}
          height={EDIT_BADGE_SIZE}
          css={{ display: 'block' }}
        />
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};
